import { AlertTriangle } from 'lucide-react'

import { spreadsheetFileTypes } from '@fortem-monorepo/constants'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { FileUploadInput } from '@fortem-monorepo/react-ui'
import { SubmitButton } from '@fortem-monorepo/react-ui'
import { Alert, AlertDescription, AlertTitle } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@fortem-monorepo/react-ui'

import { uploadMappingsFormSchema } from '@/components/forms/UploadMappingsForm/UploadMappingsSchema'
import { useOpsContext } from '@/pages/operations/ops-context.context'
import { useUploadMappingsMutation } from '@/react-query/mutations/mappings'

export interface UploadMappingsModalProps {}

export const UploadMappingsModal: React.FC<UploadMappingsModalProps> = () => {
  const context = useOpsContext()
  const uploadMappingsMutation = useUploadMappingsMutation()

  const {
    register,
    setValue,
    formState: { isSubmitting, isValid },
    reset,
    watch,
    handleSubmit,
  } = useFormWithSchema(
    {
      defaultValues: {
        files: new DataTransfer().files,
      },
    },
    uploadMappingsFormSchema,
  )

  const submitHandler = handleSubmit(async (data) => {
    await uploadMappingsMutation.mutateAsync(data)
    reset()
    context.toggleUploadMappingsModal(false)
  })

  const files = watch('files')
  const setFilesValues = (files: FileList) => setValue('files', files, { shouldValidate: true })

  return (
    <Dialog
      open={context.isUploadingMappings}
      onOpenChange={(e) => {
        context.toggleUploadMappingsModal(e)
        if (!e) {
          reset()
        }
      }}
    >
      <DialogContent className="sm:max-w-fit overflow-hidden">
        <form onSubmit={submitHandler}>
          <DialogHeader>
            <DialogTitle>Upload Mappings from Files</DialogTitle>
          </DialogHeader>
          <div className={'grid'}>
            <Alert className={'mt-3 my-5'}>
              <AlertTriangle />
              <AlertTitle>Double check</AlertTitle>
              <AlertDescription>
                <p>
                  <span className={'font-medium'}>Note: </span>ensure you have reviewed your data
                  before uploading.
                </p>
              </AlertDescription>
            </Alert>
            <FileUploadInput
              {...register('files')}
              accept={spreadsheetFileTypes.join(',')}
              files={files}
              loading={isSubmitting}
              setValue={setFilesValues}
            />
          </div>
          <DialogFooter className={'mt-4'}>
            <Button
              disabled={isSubmitting}
              onClick={(e) => {
                // e.preventDefault is required to
                // prevent submitting the form for some reason
                e.preventDefault()
                context.toggleUploadMappingsModal(false)
                reset()
              }}
              variant={'destructive'}
            >
              Cancel
            </Button>
            <SubmitButton disabled={isSubmitting || !isValid} loading={isSubmitting}>
              Upload files
            </SubmitButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
